import { type ComponentProps } from 'react';
import styled from 'styled-components';

import { DragDropProvider } from '@dnd-kit/react';
import { useSortable } from '@dnd-kit/react/sortable';

import Button from 'components/shared/Button';
import Menu from 'components/shared/menu';
import Search from 'components/shared/Search';
import { colors } from 'styles/theme/colors';
import DeactivateIcon from 'svg/DeactivateIcon';
import EllipsisIcon from 'svg/EllipsisIcon';
import EyeIcon from 'svg/EyeIcon';
import EyeOutlineIcon from 'svg/EyeOutlineIcon';
import PencilIcon from 'svg/PencilIcon';

import DragHandle from './DragHandle';

type CriteriaRow = {
  _key: string;
  criterion: string;
  description: string;
};

export type InheritedCriteriaRow = CriteriaRow & { hidden: boolean };

type DragEndHandler = NonNullable<ComponentProps<typeof DragDropProvider>['onDragEnd']>;

type Props = {
  criteria: CriteriaRow[];
  inheritedCriteria?: InheritedCriteriaRow[];
  onToggleHidden?: (key: string) => void;
  isSearching: boolean;
  searchTerm: string;
  onSearchChange: (value: string) => void;
  onAdd: () => void;
  onEdit: (key: string) => void;
  onDelete: (key: string) => void;
  onDragEnd: (...args: Parameters<DragEndHandler>) => void;
};

export default function CriteriaListView({
  criteria,
  inheritedCriteria = [],
  onToggleHidden,
  isSearching,
  searchTerm,
  onSearchChange,
  onAdd,
  onEdit,
  onDelete,
  onDragEnd,
}: Props) {
  return (
    <ListContainer>
      <SubHeader>
        <SearchWrapper>
          <Search placeholder='Search' value={searchTerm} onChange={onSearchChange} />
        </SearchWrapper>
        <Button onClick={onAdd}>Add New Criteria</Button>
      </SubHeader>
      <CriteriaTable>
        <CriteriaTableHeader>
          {!isSearching && <CriteriaTableHeaderCell style={{ flex: '0 0 40px', width: 40, padding: '16px 8px' }} />}
          <CriteriaTableHeaderCell>Criteria Label</CriteriaTableHeaderCell>
          <CriteriaTableHeaderCell>Criteria Description</CriteriaTableHeaderCell>
          <CriteriaTableHeaderCell style={{ flex: '0 0 80px', width: 80 }} />
        </CriteriaTableHeader>
        <CriteriaTableBody>
          {criteria.length === 0 && inheritedCriteria.length === 0 && (
            <CriteriaTableRow>
              <CriteriaTableCell style={{ justifyContent: 'center', flex: 1 }}>No rows found</CriteriaTableCell>
            </CriteriaTableRow>
          )}
          {inheritedCriteria.map((row) => (
            <InheritedRow key={row._key} row={row} isSearching={isSearching} onToggleHidden={onToggleHidden} />
          ))}
          {!isSearching ? (
            <DragDropProvider onDragEnd={onDragEnd}>
              {criteria.map((row, index) => (
                <SortableCriteriaRow key={row._key} row={row} index={index} onEdit={onEdit} onDelete={onDelete} />
              ))}
            </DragDropProvider>
          ) : (
            criteria.map((row) => (
              <CriteriaTableRow key={row._key}>
                <CriteriaTableCell>{row.criterion}</CriteriaTableCell>
                <CriteriaTableCell>{row.description || <>&mdash;</>}</CriteriaTableCell>
                <CriteriaTableCell style={{ flex: '0 0 80px', width: 80, justifyContent: 'flex-end' }}>
                  <RowActions rowKey={row._key} onEdit={onEdit} onDelete={onDelete} />
                </CriteriaTableCell>
              </CriteriaTableRow>
            ))
          )}
        </CriteriaTableBody>
      </CriteriaTable>
    </ListContainer>
  );
}

function RowActions({
  rowKey,
  onEdit,
  onDelete,
}: {
  rowKey: string;
  onEdit: (key: string) => void;
  onDelete: (key: string) => void;
}) {
  return (
    <Menu>
      <Menu.Trigger>
        <MenuTriggerButton data-testid='row-action-menu'>
          <EllipsisIcon width={16} height={16} style={{ transform: 'rotate(90deg)' }} />
        </MenuTriggerButton>
      </Menu.Trigger>
      <Menu.Content position='right' style={{ padding: 0, minWidth: 180 }}>
        <Menu.Item onClick={() => onEdit(rowKey)}>
          <Menu.Icon>
            <PencilIcon width={16} height={16} />
          </Menu.Icon>
          Edit Criteria
        </Menu.Item>
        <Menu.Item onClick={() => onDelete(rowKey)}>
          <Menu.Icon>
            <DeactivateIcon width={16} height={16} color={colors.red100} />
          </Menu.Icon>
          <span style={{ color: colors.red100 }}>Delete Criteria</span>
        </Menu.Item>
      </Menu.Content>
    </Menu>
  );
}

function InheritedRow({
  row,
  isSearching,
  onToggleHidden,
}: {
  row: InheritedCriteriaRow;
  isSearching: boolean;
  onToggleHidden?: (key: string) => void;
}) {
  return (
    <CriteriaTableRow style={{ opacity: row.hidden ? 0.5 : 1 }}>
      {!isSearching && <CriteriaTableCell style={{ flex: '0 0 40px', width: 40, padding: '16px 8px' }} />}
      <CriteriaTableCell>
        <LabelWithTag>
          {row.criterion}
          <InheritedTag>Inherited</InheritedTag>
        </LabelWithTag>
      </CriteriaTableCell>
      <CriteriaTableCell>{row.description || <>&mdash;</>}</CriteriaTableCell>
      <CriteriaTableCell style={{ flex: '0 0 80px', width: 80, justifyContent: 'flex-end' }}>
        <HideToggleButton
          type='button'
          onClick={() => onToggleHidden?.(row._key)}
          title={row.hidden ? 'Unhide criteria' : 'Hide criteria'}
          data-testid='inherited-criteria-hide-toggle'>
          {row.hidden ? <EyeOutlineIcon width={16} height={16} /> : <EyeIcon width={16} height={16} />}
        </HideToggleButton>
      </CriteriaTableCell>
    </CriteriaTableRow>
  );
}

function SortableCriteriaRow({
  row,
  index,
  onEdit,
  onDelete,
}: {
  row: CriteriaRow;
  index: number;
  onEdit: (key: string) => void;
  onDelete: (key: string) => void;
}) {
  const { ref, handleRef, isDragSource } = useSortable({
    id: row._key,
    index,
  });

  return (
    <CriteriaTableRow ref={ref} style={{ opacity: isDragSource ? 0.5 : 1 }}>
      <CriteriaTableCell style={{ flex: '0 0 40px', width: 40, padding: '16px 8px' }}>
        <DragHandle ref={handleRef} />
      </CriteriaTableCell>
      <CriteriaTableCell>{row.criterion}</CriteriaTableCell>
      <CriteriaTableCell>{row.description || <>&mdash;</>}</CriteriaTableCell>
      <CriteriaTableCell style={{ flex: '0 0 80px', width: 80, justifyContent: 'flex-end' }}>
        <RowActions rowKey={row._key} onEdit={onEdit} onDelete={onDelete} />
      </CriteriaTableCell>
    </CriteriaTableRow>
  );
}

const ListContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  height: 100%;
`;

const SearchWrapper = styled.div`
  width: 100%;
  max-width: 300px;
`;

const SubHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
`;

const CriteriaTable = styled.div`
  display: flex;
  flex-direction: column;
  font-size: 12px;
  border: 1px solid ${colors.black10};
  border-radius: 4px;
  overflow: hidden;
`;

const CriteriaTableHeader = styled.div`
  display: flex;
  background-color: ${colors.black3};
`;

const CriteriaTableHeaderCell = styled.div`
  display: flex;
  align-items: center;
  flex: 150 0 auto;
  width: 150px;
  padding: 16px 24px;
  font-weight: 500;
  color: ${colors.black50};
`;

const CriteriaTableBody = styled.div`
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  background-color: ${colors.white};

  > * + * {
    border-top: 1px solid ${colors.black10};
  }
`;

const CriteriaTableRow = styled.div`
  display: flex;
  flex-shrink: 0;
`;

const CriteriaTableCell = styled.div`
  display: flex;
  align-items: center;
  flex: 150 0 auto;
  width: 150px;
  padding: 16px 24px;
  white-space: initial;
`;

const MenuTriggerButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${colors.black50};
  border-radius: 4px;

  &:hover {
    background-color: ${colors.black5};
  }
`;

const HideToggleButton = styled(MenuTriggerButton)``;

const LabelWithTag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const InheritedTag = styled.span`
  font-size: 11px;
  font-weight: 500;
  color: ${colors.black75};
  border: 0.5px solid ${colors.black25};
  background-color: ${colors.black5};
  padding: 2px 8px;
  border-radius: 16px;
  white-space: nowrap;
`;
